/**
 * ToscImportPreview — Settings → "TouchOSC" section. Step 1 of the
 * eventual TouchOSC importer: pick a .tosc file, see what's in it
 * and what OPButtons can/can't represent, decide whether to import.
 *
 * This turn ships the preview only — no items get added to any
 * project. When the importer arrives, it becomes the green "Import…"
 * button at the bottom of this same panel; no rename needed.
 *
 * Bridge does the heavy lifting (zlib inflate + XML parse + analysis);
 * the PWA just renders.
 */

import { createSignal, For, Show } from 'solid-js';
import type { ToscAnalysis, UnsupportedFlag } from '@opbuttons/schema';
import { pickFile, readFileBytes } from '../bundle/download.ts';
import { parseTosc } from '../tosc/parse.ts';
import { analyze as analyzeTosc } from '../tosc/analyze.ts';

type ToastTone = 'ok' | 'err' | 'info';

interface Props {
  flash: (tone: ToastTone, text: string) => void;
}

export function ToscImportPreview(props: Props) {
  const [analysis, setAnalysis] = createSignal<ToscAnalysis | null>(null);
  const [busy, setBusy] = createSignal(false);

  async function pickAndInspect(): Promise<void> {
    const file = await pickFile('.tosc');
    if (!file) return;
    setBusy(true);
    try {
      // Parsed entirely in the browser since 2026-07-27. This used to be
      // `bridge.toscParse()` over the legacy Node bridge, which is dead, so
      // the importer failed with "not connected" on every pick. A .tosc is
      // just zlib-wrapped XML — no daemon required. See tosc/parse.ts.
      const bytes = await readFileBytes(file);
      const records = await parseTosc(bytes);
      const result = analyzeTosc(records, file.name);
      setAnalysis(result);
      props.flash('ok', `Parsed ${file.name}: ${result.counts.records} records.`);
    } catch (e) {
      props.flash('err', `Parse failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function clearAnalysis(): void {
    setAnalysis(null);
  }

  return (
    <section class="nv-ss" data-section="tosc">
      <div class="nv-ss-h"><h2>TouchOSC import preview</h2></div>
      <p class="nav-empty">
        Considering bringing a TouchOSC layout into OPButtons? Drop the .tosc file here to preview what OPButtons would import: counts, MIDI routing, OSC paths, and which controls won't translate cleanly.{' '}
        <strong>Preview only: no items get added to any project yet.</strong>
      </p>

      <button
        type="button"
        class="nv-qa nv-qa-row"
        onClick={() => { void pickAndInspect(); }}
        disabled={busy()}
      >
        <span class="nv-qa-lead">
          <svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true">
            <g fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2.5 3.5h11v9h-11z" />
              <path d="M2.5 6.5h11" />
              <path d="M5 9.5h6" />
            </g>
          </svg>
        </span>
        <span class="nv-qa-body">
          <b>{busy() ? 'Reading the file...' : 'Pick a .tosc file'}</b>
          <small>Read in your browser; no OPBridge needed</small>
        </span>
      </button>

      <Show when={analysis()}>
        {(a) => <Report analysis={a()} onClear={clearAnalysis} />}
      </Show>
    </section>
  );
}

function Report(props: { analysis: ToscAnalysis; onClear: () => void }) {
  const a = () => props.analysis;
  return (
    <div class="tosc-report">
      <header class="tosc-report-head">
        <div class="tosc-report-title">
          <span class="tosc-report-name">{a().fileName}</span>
          <span class="tosc-report-meta">{a().counts.records} records · {a().counts.byType['BUTTON'] ?? 0} buttons · {a().counts.byType['FADER'] ?? 0} faders</span>
        </div>
        <button type="button" class="tosc-report-clear" onClick={props.onClear}>
          Clear
        </button>
      </header>

      {/* Top-line counts — at-a-glance "what's in this layout." */}
      <div class="tosc-counts">
        <For each={Object.entries(a().counts.byType).sort((x, y) => y[1] - x[1])}>
          {([t, n]) => (
            <div class="tosc-count-tile">
              <span class="tosc-count-num">{n}</span>
              <span class="tosc-count-tag">{t}</span>
            </div>
          )}
        </For>
        <div class="tosc-count-tile" data-kind="midi">
          <span class="tosc-count-num">{a().counts.midiMessages}</span>
          <span class="tosc-count-tag">MIDI msg</span>
        </div>
        <div class="tosc-count-tile" data-kind="osc">
          <span class="tosc-count-num">{a().counts.oscUserMessages}</span>
          <span class="tosc-count-tag">user OSC</span>
        </div>
        <Show when={a().counts.scriptedNodes > 0}>
          <div class="tosc-count-tile" data-kind="script">
            <span class="tosc-count-num">{a().counts.scriptedNodes}</span>
            <span class="tosc-count-tag">w/ Lua</span>
          </div>
        </Show>
      </div>

      {/* Unsupported flags — the most important section for the
          inspection-first turn. Tells the user what OPButtons can't
          represent yet. */}
      <Show when={a().unsupported.length > 0}>
        <div class="tosc-block">
          <h4 class="tosc-block-title">Won't translate cleanly</h4>
          <ul class="tosc-flags">
            <For each={a().unsupported}>
              {(f: UnsupportedFlag) => (
                <li class="tosc-flag" data-code={f.code}>
                  <span class="tosc-flag-code">{f.code}</span>
                  <span class="tosc-flag-text">{f.message}</span>
                </li>
              )}
            </For>
          </ul>
        </div>
      </Show>

      {/* MIDI CC routing table — collapsed by default if long. */}
      <Show when={a().midiCcUses.length > 0}>
        <div class="tosc-block">
          <h4 class="tosc-block-title">CC routing · {a().midiCcUses.length} distinct (channel, CC) pairs</h4>
          <div class="tosc-table-wrap">
            <table class="tosc-table">
              <thead>
                <tr><th>ch</th><th>CC</th><th>used by</th><th>labels</th></tr>
              </thead>
              <tbody>
                <For each={a().midiCcUses.slice(0, 60)}>
                  {(u) => (
                    <tr>
                      <td>{u.channel}</td>
                      <td>{u.cc}</td>
                      <td>{u.uses.length}</td>
                      <td class="tosc-table-labels">{joinLabels(u.uses)}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
            <Show when={a().midiCcUses.length > 60}>
              <p class="tosc-table-more">+ {a().midiCcUses.length - 60} more pairs (preview shows first 60)</p>
            </Show>
          </div>
        </div>
      </Show>

      {/* Note routing — same shape, only shown if present. */}
      <Show when={a().midiNoteUses.length > 0}>
        <div class="tosc-block">
          <h4 class="tosc-block-title">Note routing · {a().midiNoteUses.length} distinct (channel, note) pairs</h4>
          <div class="tosc-table-wrap">
            <table class="tosc-table">
              <thead>
                <tr><th>ch</th><th>note</th><th>used by</th><th>labels</th></tr>
              </thead>
              <tbody>
                <For each={a().midiNoteUses.slice(0, 60)}>
                  {(u) => (
                    <tr>
                      <td>{u.channel}</td>
                      <td>{u.note} <span class="tosc-note-name">({u.noteName})</span></td>
                      <td>{u.uses.length}</td>
                      <td class="tosc-table-labels">{joinLabels(u.uses)}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
            <Show when={a().midiNoteUses.length > 60}>
              <p class="tosc-table-more">+ {a().midiNoteUses.length - 60} more pairs (preview shows first 60)</p>
            </Show>
          </div>
        </div>
      </Show>

      {/* User OSC routes — exclude editor-mirror noise. */}
      <Show when={a().oscRoutes.length > 0}>
        <div class="tosc-block">
          <h4 class="tosc-block-title">OSC routes · {a().oscRoutes.length} distinct paths</h4>
          <ul class="tosc-osc-list">
            <For each={a().oscRoutes}>
              {(r) => (
                <li class="tosc-osc-row">
                  <code class="tosc-osc-path">{r.path}</code>
                  <span class="tosc-osc-uses">used by {r.uses.length}</span>
                </li>
              )}
            </For>
          </ul>
        </div>
      </Show>

      {/* Groups — flat list (the parent chain reconstructs the tree). */}
      <Show when={a().groups.length > 0}>
        <div class="tosc-block">
          <h4 class="tosc-block-title">Groups · {a().groups.length}</h4>
          <ul class="tosc-group-list">
            <For each={a().groups}>
              {(g) => (
                <li class="tosc-group-row">
                  <span class="tosc-group-name">{g.name}</span>
                  <span class="tosc-group-frame">{g.frame.w}×{g.frame.h}</span>
                  <span class="tosc-group-parents">
                    {g.parents.length === 0 ? '(root)' : g.parents.join(' › ')}
                  </span>
                </li>
              )}
            </For>
          </ul>
        </div>
      </Show>
    </div>
  );
}

function joinLabels(uses: { label: string }[]): string {
  const labels = [...new Set(uses.map((u) => u.label).filter((l) => l))];
  return labels.length === 0 ? '-' : labels.slice(0, 4).join(', ') + (labels.length > 4 ? ` … +${labels.length - 4}` : '');
}
